import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Grid3x3, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { BoardClimbFormPanel } from '../components/board-dashboard/BoardClimbFormPanel';
import { BoardClimbHistoryDialog } from '../components/board-dashboard/BoardClimbHistoryDialog';
import { BoardClimbRow } from '../components/board-dashboard/BoardClimbRow';
import { BoardDashboardFilterBar } from '../components/board-dashboard/BoardDashboardFilterBar';
import { BoardGridPanel } from '../components/board-dashboard/BoardGridPanel';
import { isScannedWall, isShapedWall, scannedWallOf } from '@/components/climbs/board-grid/geometry';
import { BoardPhotoPanel } from '../components/board-dashboard/BoardPhotoPanel';
import { boardDashboardService } from '../../services/board-dashboard';
import { devicesService } from '../../services/devices';
import {
  DEFAULT_BOARD_DASHBOARD_FILTERS,
  type BoardDashboardClimb,
  type BoardDashboardFilters,
  type BoardDashboardQuery,
} from '../../types/board-dashboard';
import type { Board } from '../../types/device';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { useGymAdmin } from '../context/gym-admin-context';
import { HeaderActions } from '../layout/header-actions';
import { useResizableSplit } from '../hooks/useResizableSplit';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/** Where the reader's own split between the photo and the list is remembered. */
const SPLIT_STORAGE_KEY = 'gym_admin_board_split';

/** The `lg` breakpoint, below which the two columns stack and cannot be split. */
const TWO_COLUMNS = '(min-width: 1024px)';

/**
 * What the left column is doing: reading the wall, aligning its grid, putting a
 * problem up on it, or correcting one already there. All of them want the board
 * photo at full width, so they take turns in the same place rather than opening
 * over it.
 */
type PanelMode = 'view' | 'grid' | 'create' | 'edit';

/**
 * The panel modes as the address spells them (`?mode=`). The view is the
 * default and is left out, so a plain link to a problem is just `?problem=`.
 */
const MODE_PARAMS: Record<Exclude<PanelMode, 'view'>, string> = {
  edit: 'edit',
  create: 'new',
  grid: 'wall',
};

function modeFromParam(param: string | null): PanelMode {
  const found = Object.entries(MODE_PARAMS).find(([, value]) => value === param);
  return found ? (found[0] as PanelMode) : 'view';
}

/** Turns the filter bar's state into the query the endpoint understands. */
function toQuery(filters: BoardDashboardFilters, name: string, page: number): BoardDashboardQuery {
  return {
    page,
    per_page: PER_PAGE,
    name: name || undefined,
    grade_min: filters.gradeMin ?? undefined,
    grade_max: filters.gradeMax ?? undefined,
    inclination: filters.inclination ?? undefined,
    circuit: filters.circuit === 'all' ? undefined : filters.circuit === 'yes' ? 'true' : 'false',
    created_from: filters.createdFrom ?? undefined,
    created_to: filters.createdTo ?? undefined,
    sort: filters.sort,
    direction: filters.direction,
  };
}

/**
 * One board, managed: its photo on the left with the selected problem drawn on
 * it, its problems on the right with the numbers a setter judges them by.
 *
 * The list is filtered and sorted by the server and appended as you scroll — a
 * shared Moonboard library runs to hundreds of problems, so neither the
 * filtering nor the ordering can be something applied to an already-loaded
 * array.
 */
export default function BoardDashboardPage() {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId: string }>();
  const { loading: gymLoading, slug } = useCurrentGym();
  const { placePath } = useGymAdmin();
  const { toast } = useToast();

  const [board, setBoard] = useState<Board | null>(null);
  const [boardLoading, setBoardLoading] = useState(true);

  const [climbs, setClimbs] = useState<BoardDashboardClimb[]>([]);
  const [inclinations, setInclinations] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nameInput, setNameInput] = useState('');
  const [name, setName] = useState('');
  const [filters, setFilters] = useState<BoardDashboardFilters>(DEFAULT_BOARD_DASHBOARD_FILTERS);

  // The problem picked and what the left column is doing are the address's, so
  // a problem — or the form correcting it — can be linked to, reloaded and
  // stepped back out of with the browser.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('problem');
  const mode = modeFromParam(searchParams.get('mode'));

  const navigate = useCallback(
    (next: { problem?: string | null; mode?: PanelMode }, options?: { replace?: boolean }) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next.problem !== undefined) {
            if (next.problem) params.set('problem', next.problem);
            else params.delete('problem');
          }
          if (next.mode !== undefined) {
            if (next.mode === 'view') params.delete('mode');
            else params.set('mode', MODE_PARAMS[next.mode]);
          }
          return params;
        },
        options
      );
    },
    [setSearchParams]
  );

  // Walking the list rewrites the address rather than stacking it: Back should
  // leave the page (or the form), not replay every row the arrow keys passed.
  const setSelectedId = useCallback(
    (id: string | null) => navigate({ problem: id }, { replace: true }),
    [navigate]
  );
  const setMode = useCallback((next: PanelMode) => navigate({ mode: next }), [navigate]);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [historyClimb, setHistoryClimb] = useState<BoardDashboardClimb | null>(null);
  // A problem reached by its link that the loaded pages do not hold — further
  // down the list, or filtered out of it. Keyed by the id it was fetched for.
  const [linked, setLinked] = useState<{ id: string; climb: BoardDashboardClimb } | null>(null);
  // Bumped when this page itself puts a problem up, to pull the list back from
  // the server: the new one has to appear in it under whatever filters are on,
  // which is the server's answer to give and not one to splice in here.
  const [reloadToken, setReloadToken] = useState(0);
  // Bumped to start the grid panel over on the board as just saved.
  const [gridPanelKey, setGridPanelKey] = useState(0);

  const split = useResizableSplit(SPLIT_STORAGE_KEY, t('gymAdmin.boardDashboard.resizeColumns'));
  const twoColumns = useMediaQuery(TWO_COLUMNS);

  // Typing a name should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setName(nameInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [nameInput]);

  // The board itself, for the photo, the grid geometry and the grade ladder the
  // filter bar picks its range off. It comes from the boards list rather than a
  // dedicated endpoint — the panel already reads that list.
  useEffect(() => {
    if (!slug || !boardId) return;

    let cancelled = false;
    setBoardLoading(true);

    devicesService
      .listGymBoards(slug)
      .then((boards) => {
        if (!cancelled) setBoard(boards.find((candidate) => candidate.id === boardId) ?? null);
      })
      .catch(() => {
        if (!cancelled) setBoard(null);
      })
      .finally(() => {
        if (!cancelled) setBoardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, boardId]);

  const query = useCallback(
    (page: number) => toQuery(filters, name, page),
    [filters, name]
  );

  // Which page the scroll has reached. A ref because loadMore reads it at the
  // moment it fires, not at the moment it was built.
  const pageRef = useRef(1);

  // First page, and every time a filter or the ordering changes.
  useEffect(() => {
    if (!slug || !boardId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    pageRef.current = 1;

    boardDashboardService
      .listClimbs(slug, boardId, query(1))
      .then((page) => {
        if (cancelled) return;
        setClimbs(page.climbs);
        setHasMore(page.hasMore);
        setInclinations(page.inclinations);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setClimbs([]);
        setHasMore(false);
        // The raw message, translated at render: `t` in this effect's
        // dependencies would re-run the load on every language object identity.
        setError(err instanceof Error ? err.message : '');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, boardId, query, reloadToken]);

  // The scroll sentinel can fire again before a page lands, so the guard is a
  // ref rather than state: it has to be true the moment the request goes out.
  const loadingMoreRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (!slug || !boardId || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const nextPage = pageRef.current + 1;
      const page = await boardDashboardService.listClimbs(slug, boardId, query(nextPage));
      pageRef.current = nextPage;
      setClimbs((prev) => [...prev, ...page.climbs]);
      setHasMore(page.hasMore);
    } catch {
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [slug, boardId, query]);

  // Kept in a ref so the observer below survives a new loadMore identity.
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current();
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, loading]);

  // A selection survives paging and re-filtering as long as the problem is
  // still on the list; the variants are searched too, since selecting one of
  // those is how the angle cards work.
  const listed = useMemo(() => {
    if (!selectedId) return null;

    for (const climb of climbs) {
      if (climb.id === selectedId) return climb;
      const variant = climb.variants?.find((candidate) => candidate.id === selectedId);
      if (variant) return variant;
    }

    return null;
  }, [climbs, selectedId]);

  const linkedClimb = linked && linked.id === selectedId ? linked.climb : null;
  const selected = listed ?? linkedClimb;

  // A link to a problem the list has not loaded is answered by the problem's
  // own endpoint, once the first page is in and has not got it.
  useEffect(() => {
    if (!slug || !boardId || !selectedId || loading || listed || linked?.id === selectedId) return;

    let cancelled = false;
    boardDashboardService
      .getClimbDetail(slug, boardId, selectedId)
      .then((detail) => {
        if (!cancelled) setLinked({ id: selectedId, climb: detail.climb });
      })
      .catch(() => {
        if (cancelled) return;
        toast({ title: t('gymAdmin.boardDashboard.problemNotFound'), variant: 'destructive' });
        navigate({ problem: null, mode: 'view' }, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [slug, boardId, selectedId, loading, listed, linked, navigate, toast, t]);

  // What the arrow keys walk: the rows as they are drawn, with an expanded
  // row's angles following it — so stepping down goes wherever the eye does.
  const navigable = useMemo(() => {
    const rows: BoardDashboardClimb[] = [];

    climbs.forEach((climb) => {
      rows.push(climb);
      if (expandedId === climb.id) rows.push(...(climb.variants ?? []));
    });

    return rows;
  }, [climbs, expandedId]);

  // Up and down move the selection once something is selected, which is what
  // makes the photo panel a viewer: pick a problem, then walk the wall.
  useEffect(() => {
    if (!selectedId || navigable.length === 0) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;

      // Typing in the name filter, picking a date or reading the history modal
      // is not list navigation.
      const target = event.target as HTMLElement | null;
      if (
        target?.closest?.(
          'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="listbox"]'
        )
      ) {
        return;
      }

      let index = navigable.findIndex((row) => row.id === selectedId);
      if (index === -1) {
        // The selection came from an angle card while its row is collapsed, so
        // it is not drawn in the list: step from the row that owns it.
        index = navigable.findIndex((row) =>
          row.variants?.some((variant) => variant.id === selectedId)
        );
        if (index === -1) return;
      }

      const next = navigable[index + (event.key === 'ArrowDown' ? 1 : -1)];
      if (!next) return;

      // Only once there is somewhere to go: at either end the key should still
      // scroll the list, as it would with nothing selected.
      event.preventDefault();
      setSelectedId(next.id);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigable, selectedId, setSelectedId]);

  // The left column keeps whatever scroll the mode before it left behind, and
  // the button that opens the form sits *under* the photo — so a setter has
  // scrolled down to reach it, and the form would open somewhere in its middle
  // with the name and the grade above the fold. Every mode starts at its top.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // jsdom gives the node no scrolling to reset, and neither has an unmounted
    // column.
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [mode]);

  // Keeps a selection reached by keyboard on screen. `nearest` so clicking a
  // row that is already visible does not jerk the list around.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedId) return;

    const row = listRef.current?.querySelector(`[data-climb-id="${selectedId}"]`);
    // jsdom has no scrollIntoView, and neither has an unmounted row.
    (row as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedId]);

  if (gymLoading || boardLoading) {
    return <Skeleton className="h-[70vh] w-full" />;
  }

  if (!board) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          {t('gymAdmin.boardDashboard.boardNotFound')}
          <div className="mt-4">
            <Button variant="outline" asChild>
              <Link to={placePath('/boards')}>
                <ArrowLeft className="mr-2 size-4" />
                {t('gymAdmin.boardDashboard.backToBoards')}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // The handle between the two columns, built once: the form takes both columns
  // over while it is open and draws the same handle between its own halves, so
  // the split the reader set does not jump about as the panel changes.
  const separator = (
    <div
      {...split.separatorProps}
      className={cn(
        'group hidden cursor-col-resize touch-none items-stretch justify-center lg:flex',
        'focus-visible:outline-none'
      )}
    >
      <span
        className={cn(
          'my-2 w-1 rounded-full bg-border transition-colors',
          'group-hover:bg-muted-foreground/50 group-focus-visible:bg-primary',
          split.dragging && 'bg-primary'
        )}
        aria-hidden
      />
    </div>
  );

  const grades = board.grading_system?.grades ?? [];

  const renderRow = (climb: BoardDashboardClimb) => (
    <BoardClimbRow
      key={climb.id}
      climb={climb}
      selectedId={selectedId}
      expanded={expandedId === climb.id}
      showInclination={board.adjustable_inclination}
      // The problem is corrected from its own row, so the form opens on
      // whatever the reader is pointing at.
      canEdit={canEditProblems}
      onEdit={(picked) => navigate({ problem: picked.id, mode: 'edit' })}
      onOpenHistory={setHistoryClimb}
      onSelect={(picked) => setSelectedId(picked.id)}
      onToggleVariants={(id) => setExpandedId((current) => (current === id ? null : id))}
    />
  );
  const imageUrl = board.image_url ?? board.board_type.image_url ?? null;
  const rows = board.rows ?? board.board_type.rows ?? 0;
  const columns = board.columns ?? board.board_type.columns ?? 0;
  // Both jobs need somewhere to put the holds: a photo, and either a grid ruled
  // on it or the lines a wall is drawn with — a wall described by lines carries
  // its holds on them and has no rows or columns to speak of.
  // And a wall described by its photo's scan has its holds traced on it
  // (docs/SPRAY_WALL_SCAN_PLAN.md).
  const hasGrid =
    !!imageUrl &&
    (isScannedWall(scannedWallOf(board.scanned_holds)) || isShapedWall(board.shapes) || (rows > 0 && columns > 0));
  // A mapped layout (Kilter) is aligned by its manufacturer against a picture
  // that travels with it. There is nothing for a gym to move — and moving it
  // would misplace every problem in the shared library.
  //
  // A photo is enough to open the editor: an unruled wall with nothing on it yet
  // is exactly the one somebody goes in there to draw the lines of.
  const ownsItsGrid = !!imageUrl && !board.layout_from_board_type;
  // A global board shows the worldwide library for its type. Those problems
  // belong to every gym that has the wall, so correcting one here would correct
  // it for all of them — the server refuses it, and so does the button.
  const canEditProblems = hasGrid && !board.global;
  // Whether the form is up. An edit needs the problem it corrects: the
  // selection can go with a filter change, and there is nothing to edit then.
  const formOpen = hasGrid && (mode === 'create' || (mode === 'edit' && !!selected));
  // The other panel that takes both columns over. Only a wall whose grid this
  // gym owns has one to align — see ownsItsGrid.
  const gridOpen = mode === 'grid' && ownsItsGrid;

  return (
    // The shell pins itself to the window, so the page can take the height it
    // is given and hand each column its own scrollbar. Only from `lg`: two
    // short scroll areas stacked on a phone would be worse than one page.
    <div className="flex flex-col gap-4 lg:h-full">
      {/* The two things done *to* the wall, as opposed to read off it, in the
          shell's header. Both take over the columns below, so each one is a
          toggle back out. */}
      <HeaderActions>
        {ownsItsGrid && (
          <Button
            variant={mode === 'grid' ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={mode === 'grid'}
            onClick={() => setMode(mode === 'grid' ? 'view' : 'grid')}
          >
            <Grid3x3 className="mr-2 size-4" aria-hidden />
            {t('gymAdmin.boardDashboard.grid.edit')}
          </Button>
        )}

        {hasGrid && (
          <Button
            size="sm"
            variant={mode === 'create' ? 'secondary' : 'default'}
            aria-pressed={mode === 'create'}
            onClick={() => setMode(mode === 'create' ? 'view' : 'create')}
          >
            <Plus className="mr-2 size-4" aria-hidden />
            {t('gymAdmin.boardDashboard.newProblem')}
          </Button>
        )}
      </HeaderActions>

      <div
        ref={split.containerRef}
        // Two columns whose share of the window the reader sets: a dense wall
        // is unreadable in a fixed 26rem, and somebody scanning a few hundred
        // problems wants the list instead. Below `lg` they stack, and there is
        // nothing to split.
        className="flex flex-col gap-4 lg:grid lg:min-h-0 lg:flex-1 lg:gap-0"
        style={
          twoColumns
            ? { gridTemplateColumns: `${split.width}px 1.25rem minmax(0,1fr)` }
            : undefined
        }
      >
        {/* Putting a problem up, or correcting one, takes both columns: the
            wall stays where it was and the form gets the room the list had.
            The list is a click away and the problem is what is being worked
            on, so it is the one that gives its column up. */}
        {formOpen && slug && imageUrl ? (
          <BoardClimbFormPanel
            gymSlug={slug}
            board={board}
            imageUrl={imageUrl}
            editing={mode === 'edit' ? selected : null}
            separator={separator}
            onCreated={(created) => {
              // Straight onto the wall it was just drawn on, so the panel
              // shows the problem that was saved rather than an empty grid.
              // In place of the form's entry: Back should not reopen it.
              navigate({ problem: created.id, mode: 'view' }, { replace: true });
              setReloadToken((token) => token + 1);
            }}
            onEdited={() => {
              navigate({ mode: 'view' }, { replace: true });
              // A corrected problem may have moved under the filters and the
              // sort, so the list is the server's to redraw — and one reached
              // by its link is fetched again.
              setLinked(null);
              setReloadToken((token) => token + 1);
            }}
            onDelete={
              mode === 'edit' && selected
                ? async () => {
                    try {
                      await boardDashboardService.deleteClimb(slug, board.id, selected.id);
                    } catch (err) {
                      toast({
                        title: t('gymAdmin.boardDashboard.deleteError'),
                        description: err instanceof Error ? err.message : undefined,
                        variant: 'destructive',
                      });
                      throw err;
                    }
                    toast({ title: t('gymAdmin.boardDashboard.deleted') });
                    navigate({ problem: null, mode: 'view' }, { replace: true });
                    setLinked(null);
                    setReloadToken((token) => token + 1);
                  }
                : undefined
            }
            onCancel={() => setMode('view')}
          />
        ) : gridOpen && slug && imageUrl ? (
          /* Aligning the grid takes both columns for the same reason: the wall
             is what the work is being done on, and a list of problems has
             nothing to say about where the grid sits on it. */
          <BoardGridPanel
            // Remounted on a save that keeps it open, so it starts from the
            // wall as the server now has it — a new photo, its scan running.
            key={gridPanelKey}
            gymSlug={slug}
            board={board}
            // The whole upload, not the window cut out of it: the crop is one
            // of the things edited in there, so it has to be able to show what
            // a previous one cut away.
            imageUrl={board.original_image_url ?? imageUrl}
            separator={separator}
            onSaved={(saved, climbsCleared, newPhoto) => {
              // Saving keeps the panel open: it is a save of work in progress,
              // and "Cancel" is the way out. A new photo starts it over, on the
              // new wall and its scan.
              setBoard(saved);
              if (newPhoto) setGridPanelKey((key) => key + 1);
              // The problems were taken off the wall: the list is the server's
              // answer under whatever filters are on, so it is pulled back
              // rather than emptied here.
              if (climbsCleared) setReloadToken((token) => token + 1);
            }}
            onCancel={() => setMode('view')}
          />
        ) : (
          <>
            {/* Left: the wall, scrolling on its own — walking the list must not
                carry the photo off the top of the screen. */}
            <div ref={panelRef} className="lg:min-h-0 lg:overflow-y-auto">
              <BoardPhotoPanel board={board} selected={selected} />
            </div>

            {/* The handle between them. Its column is the whole gutter, so
                there is something to grab without a hairline to aim at. */}
            {separator}

            <div className="flex flex-col gap-3 lg:min-h-0">
              <div className="lg:shrink-0">
                <BoardDashboardFilterBar
                  filters={filters}
                  onChange={setFilters}
                  grades={grades}
                  inclinations={board.adjustable_inclination ? inclinations : []}
                  nameInput={nameInput}
                  onNameInputChange={setNameInput}
                />
              </div>

              <Card ref={listRef} className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="space-y-2 p-4">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={index} className="h-14 w-full" />
                      ))}
                    </div>
                  ) : error !== null ? (
                    <p className="p-12 text-center text-sm text-destructive">
                      {error || t('errors.unexpectedError')}
                    </p>
                  ) : climbs.length === 0 ? (
                    <p className="p-12 text-center text-sm text-muted-foreground">
                      {t('gymAdmin.boardDashboard.noMatches')}
                    </p>
                  ) : (
                    <>
                      {/* A problem opened by its link that the loaded pages do
                          not hold, pinned above them so its row is there to
                          read and to edit from. */}
                      {!listed && linkedClimb && renderRow(linkedClimb)}
                      {climbs.map(renderRow)}

                      {hasMore && (
                        <div
                          ref={sentinelRef}
                          className="flex justify-center p-4"
                          data-testid="board-dashboard-sentinel"
                        >
                          {loadingMore && (
                            <Loader2 className="size-5 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>

      {slug && boardId && (
        <BoardClimbHistoryDialog
          gymSlug={slug}
          boardId={boardId}
          climb={historyClimb}
          onClose={() => setHistoryClimb(null)}
        />
      )}
    </div>
  );
}
